import { useCallback, useRef, useState, type CSSProperties } from 'react';

// Zoom and pan for the photo viewer.
//
// Pinch, pan and swipe are one gesture system on one surface — which of the three
// a touch turns out to be is decided by how many fingers landed and whether the
// photo is already zoomed — so they are handled together rather than by three
// components fighting over the same events.

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;
/** What one press of a zoom button is worth. */
const ZOOM_STEP = 0.5;
/** What a double tap jumps to, and back from. */
const DOUBLE_TAP_ZOOM = 2;
/** How far a touch has to travel sideways before it counts as a page. */
const SWIPE_THRESHOLD_PX = 50;

interface Point {
  x: number;
  y: number;
}

/** Which gesture a touch turned out to be, decided when the fingers land. */
type Gesture =
  | { kind: 'none' }
  | { kind: 'swipe'; start: Point }
  | { kind: 'pan'; start: Point; startPan: Point }
  | { kind: 'pinch'; startDistance: number; startZoom: number };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function touchPoint(touch: { clientX: number; clientY: number }): Point {
  return { x: touch.clientX, y: touch.clientY };
}

function pinchDistance(a: React.Touch, b: React.Touch): number {
  return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
}

/**
 * The fingers this event is about.
 *
 * `touches` is the authority — it is the only list that says how many fingers are
 * down, which is what separates a pinch from a drag. But a finger that has just
 * landed or just lifted is in `changedTouches`, and on touchend `touches` no
 * longer holds it at all, so the fallback is what keeps a one-finger gesture
 * readable from either end of it.
 */
function touchesOf(event: React.TouchEvent): React.TouchList | React.Touch[] {
  return event.touches.length > 0 ? event.touches : event.changedTouches;
}

interface UseImageZoomOptions {
  /**
   * Turn the page. Omitted where there is no page to turn — a receipt is looked
   * at on its own — and a horizontal drag then does nothing rather than
   * silently moving through a set of one.
   */
  onSwipe?: (delta: 1 | -1) => void;
}

export function useImageZoom({ onSwipe }: UseImageZoomOptions = {}) {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });

  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const gesture = useRef<Gesture>({ kind: 'none' });
  // Mouse panning has no touch list to read state from, so it carries its own.
  const mousePan = useRef<{ start: Point; startPan: Point } | null>(null);

  /**
   * Keep the photo from being dragged out of sight.
   *
   * The slack in each axis is what the scaled image overflows its box by, so at
   * 1× there is none and the photo can't be nudged off centre at all.
   */
  const clampPan = useCallback((next: Point, atZoom: number): Point => {
    const box = surfaceRef.current;
    if (!box) return next;
    const maxX = (box.clientWidth * (atZoom - 1)) / 2;
    const maxY = (box.clientHeight * (atZoom - 1)) / 2;
    return { x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
  }, []);

  const applyZoom = useCallback(
    (next: number) => {
      const target = clamp(next, MIN_ZOOM, MAX_ZOOM);
      setZoom(target);
      // Back at 1× there is nowhere to pan to, so the photo re-centres rather
      // than staying where it was dragged.
      setPan((current) => (target === MIN_ZOOM ? { x: 0, y: 0 } : clampPan(current, target)));
    },
    [clampPan],
  );

  const zoomIn = useCallback(() => applyZoom(zoom + ZOOM_STEP), [applyZoom, zoom]);
  const zoomOut = useCallback(() => applyZoom(zoom - ZOOM_STEP), [applyZoom, zoom]);
  const reset = useCallback(() => {
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
    gesture.current = { kind: 'none' };
    mousePan.current = null;
  }, []);

  const toggleZoom = useCallback(
    () => applyZoom(zoom > MIN_ZOOM ? MIN_ZOOM : DOUBLE_TAP_ZOOM),
    [applyZoom, zoom],
  );

  const handleTouchStart = (event: React.TouchEvent) => {
    const touches = touchesOf(event);
    const [first, second] = [touches[0], touches[1]];
    if (first && second) {
      gesture.current = {
        kind: 'pinch',
        startDistance: pinchDistance(first, second),
        startZoom: zoom,
      };
      return;
    }
    if (!first) return;
    // Zoomed in, a drag moves the photo; at 1× it turns the page. The same
    // gesture can't sensibly do both.
    if (zoom > MIN_ZOOM) {
      gesture.current = { kind: 'pan', start: touchPoint(first), startPan: pan };
      return;
    }
    gesture.current = onSwipe ? { kind: 'swipe', start: touchPoint(first) } : { kind: 'none' };
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    const current = gesture.current;

    if (current.kind === 'pinch') {
      const touches = touchesOf(event);
      const [first, second] = [touches[0], touches[1]];
      if (!first || !second) return;
      const distance = pinchDistance(first, second);
      if (current.startDistance === 0) return;
      applyZoom(current.startZoom * (distance / current.startDistance));
      return;
    }

    if (current.kind === 'pan') {
      const first = touchesOf(event)[0];
      if (!first) return;
      setPan(
        clampPan(
          {
            x: current.startPan.x + (first.clientX - current.start.x),
            y: current.startPan.y + (first.clientY - current.start.y),
          },
          zoom,
        ),
      );
    }
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    const current = gesture.current;
    gesture.current = { kind: 'none' };
    if (current.kind !== 'swipe') return;

    const end = event.changedTouches[0];
    if (!end) return;
    const dx = end.clientX - current.start.x;
    const dy = end.clientY - current.start.y;
    // A tap and a vertical drag both mean something other than "next photo".
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;
    // Dragging left pulls the next photo in, as a photo roll does.
    onSwipe?.(dx < 0 ? 1 : -1);
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (zoom <= MIN_ZOOM) return;
    event.preventDefault();
    mousePan.current = { start: { x: event.clientX, y: event.clientY }, startPan: pan };
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    const drag = mousePan.current;
    if (!drag) return;
    setPan(
      clampPan(
        {
          x: drag.startPan.x + (event.clientX - drag.start.x),
          y: drag.startPan.y + (event.clientY - drag.start.y),
        },
        zoom,
      ),
    );
  };

  const endMousePan = () => {
    mousePan.current = null;
  };

  // Radix locks body scroll while the dialog is open, so the wheel has nothing
  // else to do here.
  const handleWheel = (event: React.WheelEvent) => {
    applyZoom(zoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
  };

  const isZoomed = zoom > MIN_ZOOM;

  const imageStyle: CSSProperties = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
  };

  return {
    zoom,
    isZoomed,
    canZoomIn: zoom < MAX_ZOOM,
    canZoomOut: zoom > MIN_ZOOM,
    zoomIn,
    zoomOut,
    reset,
    imageStyle,
    surfaceRef,
    surfaceProps: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onDoubleClick: toggleZoom,
      onWheel: handleWheel,
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: endMousePan,
      onMouseLeave: endMousePan,
    },
  };
}
